/**
 * TTC Subway Map - Event Management System
 * 
 * This file contains all event-related functionality including:
 * - Station click handlers
 * - Button event listeners
 * - Scroll and resize handlers
 * - Tooltip and drawer interactions
 * - Line selector functionality
 */

import { iconFont } from './icon-fonts.js';

// Global state for event management
let currentTooltipStation = null;
let useShortNames = false;
let activeLineButton = "line1";
let activeBranchButton = "line1-university";
let scrollTimeout;
let resizeTimeout;
let isDrawingMap = false;
let isDrawingResponsiveMap = false;

const SVG_ID_TO_LINE_KEY = {
    "line1university-svg": "line1University",
    "line1-university-svg": "line1University",
    "line1yonge-svg": "line1Yonge",
    "line1-yonge-svg": "line1Yonge",
    "line2-svg": "line2",
    "line2-bloor-danforth-svg": "line2",
    "line4-svg": "line4",
    "line4-sheppard-svg": "line4",
    "line5-svg": "line5",
    "line5-eglinton-svg": "line5",
    "line6-svg": "line6",
    "line6-finch-west-svg": "line6",
    "line6-finchwest-svg": "line6",
};

const LINE_COLORS = {
    default: "#da251d",
    line1: "#FFD320", // Yellow
    line1University: "#FFD320", // Yellow
    line1Yonge: "#FFD320", // Yellow
    line2: "#00B04F", // Green
    line3: "#00a4e3", // Blue
    line4: "#a21a68", // Purple
    line5: "#eb8738", // Orangeish
    line6: "#969594", // Distopian Grey
};

/**
 * Station Click Event Handler
 * Handles clicks on station circles and labels
 */
function handleStationClick(event, station, svgId = null) {
  event.stopPropagation();
  currentTooltipStation = station;

  console.log('Station clicked:', station.name, 'SVG ID:', svgId);

  // Use unified drawer for all screen sizes
  console.log('Calling showStationDrawer');
  if (typeof window.showStationDrawer === 'function') {
    window.showStationDrawer(station, svgId);
  } else {
    console.error('showStationDrawer function not available');
  }
}

/**
 * Toggle Short Names Button Handler
 */
async function toggleShortNames() {
  useShortNames = !useShortNames;
  console.log("useShortNames toggled to:", useShortNames);

  const btn = document.getElementById("shortNameBtn");
  btn.classList.toggle("active", useShortNames);

  // Update button content with icon
  const icon = iconFont('text_fields', 'text-sm');
  btn.innerHTML = useShortNames ?
    `${icon} Use Full Names` :
    `${icon} Use Short Names`;

  // Redraw map to apply changes
  if (typeof drawMap === 'function') {
    drawMap();
  } else if (typeof drawResponsiveMap === 'function') {
    drawResponsiveMap();
  }
}

/**
 * Line Selector Button Event Handlers
 */
function setupLineSelectorEvents() {
  // Main line buttons event delegation
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[id$="-button"]');
    if (!button || !button.id.match(/^(line[1-4])-button$/)) return;

    const lineId = button.id.replace('-button', '');

    // Update active line
    activeLineButton = lineId;
    activeBranchButton = null; // Reset branch selection when switching lines

    // Update button styles
    updateLineSelectorButtonStyles(lineId);

    // Show/hide containers and update visibility
    updateLineVisibility();
    updateBranchButtonsVisibility();
  });

  // Branch buttons event delegation
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[id*="line1-"][id$="-button"]');
    if (!button || !button.id.match(/^line1-(university|yonge)-button$/)) return;

    // Only allow branch button functionality on small screens (xs)
    const isSmallScreen = window.matchMedia('(max-width: 639px)').matches;
    if (!isSmallScreen) return;

    const branchId = button.id.replace('-button', '');

    // Update active branch
    activeBranchButton = branchId;

    // Update branch button styles
    updateBranchButtonStyles(branchId);

    // Scroll to the appropriate branch
    scrollToBranch(branchId);
  });
}

/**
 * Update Line Selector Button Styles
 */
function updateLineSelectorButtonStyles(activeLineId) {
  const lines = [
    { id: "line1", color: LINE_COLORS.line1 },
    { id: "line2", color: LINE_COLORS.line2 },
    { id: "line4", color: LINE_COLORS.line4 },
    { id: "line5", color: LINE_COLORS.line5 },
    { id: "line6", color: LINE_COLORS.line6 }
  ];

  document.querySelectorAll("#lineSelectorButtons button").forEach(btn => {
    const btnLineId = btn.id.replace("-button", "");
    const btnLine = lines.find(l => l.id === btnLineId);

    if (!btnLine) return;

    if (btnLineId === activeLineId) {
      btn.style.backgroundColor = btnLine.color;
      btn.style.color = "#1F2937";
      btn.classList.remove("btn-line-inactive");
      btn.classList.add("btn-line-active");
    } else {
      btn.style.backgroundColor = "";
      btn.style.color = "";
      btn.classList.remove("btn-line-active");
      btn.classList.add("btn-line-inactive");
    }
  });

  // Reset branch button styles
  document.querySelectorAll("#line1BranchButtons button").forEach(btn => {
    btn.style.backgroundColor = "#6B7280";
    btn.style.color = "#F3F4F6";
  });
}

/**
 * Update Branch Button Styles
 */
function updateBranchButtonStyles(activeBranchId) {
  const branchColor = "#FFD320";

  document.querySelectorAll("#line1BranchButtons button").forEach(btn => {
    const btnBranchId = btn.id.replace("-button", "");

    if (btnBranchId === activeBranchId) {
      btn.style.backgroundColor = branchColor;
      btn.style.color = "#1F2937";
    } else {
      btn.style.backgroundColor = "#6B7280";
      btn.style.color = "#F3F4F6";
    }
  });
}

/**
 * Scroll to Branch Handler
 */
function scrollToBranch(branchId) {
  const targetElement = document.getElementById(`${branchId}-svg`);
  if (targetElement) {
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }
}

/**
 * Update Line Visibility Based on Screen Size and Active Button
 */
function updateLineVisibility() {
  const containers = document.querySelectorAll(".line-container");
  const isSmallScreen = window.matchMedia('(max-width: 639px)').matches;
  const isMediumScreen = window.matchMedia('(min-width: 640px) and (max-width: 1023px)').matches;
  const isLargeScreen = window.matchMedia('(min-width: 1024px)').matches;

  if (isSmallScreen || isMediumScreen) {
    // Show only active container on small/medium screens
    containers.forEach(container => {
      if (container.id === activeLineButton + "-container") {
        container.style.display = "flex";
        container.style.opacity = "1";
        container.style.transform = "scale(1)";
      } else {
        container.style.display = "none";
      }
    });
  } else if (isLargeScreen) {
    // Show all containers on large screens
    containers.forEach(container => {
      container.style.display = "flex";
      container.style.opacity = "1";
      container.style.transform = "scale(1)";
    });
  }
}

/**
 * Update Branch Buttons Visibility
 */
function updateBranchButtonsVisibility() {
  const branchButtons = document.getElementById("line1BranchButtons");
  if (!branchButtons) return;

  const isSmallScreen = window.matchMedia('(max-width: 639px)').matches;

  if (activeLineButton === "line1" && isSmallScreen) {
    branchButtons.style.display = "flex";

    // Update active branch button styling
    const universityButton = document.getElementById("line1-university-button");
    const yongeButton = document.getElementById("line1-yonge-button");

    if (universityButton && yongeButton) {
      updateBranchButtonStyles(activeBranchButton);
    }
  } else {
    branchButtons.style.display = "none";
  }
}

/**
 * Slow Zone Click Handler
 * Shows detailed tooltip when slow zone is clicked
 */
function showSlowZoneTooltip(event, slowZone, lineData) {
  // Remove any existing slow zone tooltip
  const existingTooltip = document.getElementById('slowZoneTooltip');
  if (existingTooltip) {
    existingTooltip.remove();
  }

  // Create tooltip container
  const tooltip = document.createElement('div');
  tooltip.id = 'slowZoneTooltip';
  tooltip.className = 'fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-sm';
  tooltip.style.pointerEvents = 'auto';

  // Get click position
  const x = event.clientX;
  const y = event.clientY;

  // Create tooltip content
  const content = `
    <div class="slow-zone-tooltip-content">
      <div class="flex items-center gap-2 mb-3">
        <div class="w-6 h-1 bg-red-600 rounded" style="background: linear-gradient(90deg, #DC143C 50%, transparent 50%); background-size: 8px 100%;"></div>
        <h3 class="text-lg font-semibold text-gray-800">Slow Zone Alert 🐢</h3>
      </div>
      
      <div class="space-y-2 text-sm">
        <div class="flex justify-between">
          <span class="font-medium text-gray-600">Line:</span>
          <span class="text-gray-800">${lineData.name || 'Unknown'}</span>
        </div>
        
        <div class="flex justify-between">
          <span class="font-medium text-gray-600">From:</span>
          <span class="text-gray-800">${slowZone.from}</span>
        </div>
        
        <div class="flex justify-between">
          <span class="font-medium text-gray-600">To:</span>
          <span class="text-gray-800">${slowZone.to}</span>
        </div>
        
        <div class="flex justify-between">
          <span class="font-medium text-gray-600">Direction:</span>
          <span class="text-gray-800 capitalize">${slowZone.direction}</span>
        </div>
      </div>
      
      <div class="mt-3 p-2 bg-red-50 rounded border-l-4 border-red-400">
        <p class="text-sm text-red-700">
          <span class="font-medium">⚠️ Service Impact:</span> Trains are operating at reduced speeds in this section, which may cause delays.
        </p>
      </div>
      
      <div class="mt-3 text-xs text-gray-500">
        Click anywhere outside to close
      </div>
    </div>
  `;

  tooltip.innerHTML = content;
  document.body.appendChild(tooltip);

  // Position tooltip
  positionTooltip(tooltip, x, y);

  // Setup tooltip close handlers
  setupTooltipCloseHandlers(tooltip);
}

/**
 * Position Tooltip Helper
 */
function positionTooltip(tooltip, x, y) {
  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = x - tooltipRect.width / 2;
  let top = y - tooltipRect.height - 10;

  // Adjust if tooltip would go off screen
  if (left < 10) left = 10;
  if (left + tooltipRect.width > viewportWidth - 10) left = viewportWidth - tooltipRect.width - 10;
  if (top < 10) top = y + 10;
  if (top + tooltipRect.height > viewportHeight - 10) top = viewportHeight - tooltipRect.height - 10;

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;

  // Add entrance animation
  tooltip.style.opacity = '0';
  tooltip.style.transform = 'scale(0.9)';
  tooltip.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';

  setTimeout(() => {
    tooltip.style.opacity = '1';
    tooltip.style.transform = 'scale(1)';
  }, 10);
}

/**
 * Setup Tooltip Close Handlers
 */
function setupTooltipCloseHandlers(tooltip) {
  const closeTooltip = (e) => {
    if (!tooltip.contains(e.target)) {
      tooltip.style.opacity = '0';
      tooltip.style.transform = 'scale(0.9)';
      setTimeout(() => {
        if (tooltip.parentNode) {
          tooltip.remove();
        }
        document.removeEventListener('click', closeTooltip);
      }, 200);
    }
  };

  const closeOnEscape = (e) => {
    if (e.key === 'Escape') {
      closeTooltip({ target: document.body });
      document.removeEventListener('keydown', closeOnEscape);
    }
  };

  setTimeout(() => {
    document.addEventListener('click', closeTooltip);
  }, 100);

  document.addEventListener('keydown', closeOnEscape);
}

/**
 * Scroll Event Handler for Line Detection
 */
function handleLineScroll(event) {
  if (!window.matchMedia('(max-width: 639px)').matches) {
    return;
  }

  clearTimeout(scrollTimeout);

  scrollTimeout = setTimeout(() => {
    const container = event.target;
    const lineContainers = document.querySelectorAll('.line-container');
    let mostVisibleLine = null;
    let maxVisibleWidth = 0;

    // Find most visible container
    lineContainers.forEach(lineContainer => {
      const rect = lineContainer.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const leftEdge = Math.max(rect.left, containerRect.left);
      const rightEdge = Math.min(rect.right, containerRect.right);
      const visibleWidth = Math.max(0, rightEdge - leftEdge);

      if (visibleWidth > maxVisibleWidth) {
        maxVisibleWidth = visibleWidth;
        mostVisibleLine = lineContainer.id.replace('-container', '');
      }
    });

    // Update active line based on scroll position
    updateActiveLineFromScroll(mostVisibleLine);
  }, 150);
}

/**
 * Update Active Line From Scroll Detection
 */
function updateActiveLineFromScroll(mostVisibleLine) {
  let newActiveLineButton = mostVisibleLine;
  let newActiveBranchButton = activeBranchButton;

  // Handle Line 1 branches
  if (mostVisibleLine === "line1-university" || mostVisibleLine === "line1-yonge") {
    newActiveLineButton = "line1";
    newActiveBranchButton = mostVisibleLine;
  }

  // Update if different from current active line
  if (newActiveLineButton && newActiveLineButton !== activeLineButton) {
    activeLineButton = newActiveLineButton;
    activeBranchButton = newActiveBranchButton;

    updateLineSelectorButtonStyles(activeLineButton);
    updateBranchButtonsVisibility();
  }
  // Handle Line 1 branch changes
  else if (activeLineButton === "line1" &&
    (mostVisibleLine === "line1-university" || mostVisibleLine === "line1-yonge") &&
    mostVisibleLine !== activeBranchButton) {

    activeBranchButton = mostVisibleLine;
    updateBranchButtonStyles(activeBranchButton);
  }
}

/**
 * Resize Event Handler
 */
function handleWindowResize() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    const isSmallViewport = window.matchMedia('(max-width: 639px)').matches;

    window.lastKnownViewportSize = isSmallViewport ? 'sm' : 'md-or-larger';

    // Update visibility based on new screen size
    updateLineVisibility();
    updateBranchButtonsVisibility();
  }, 250);
}

/**
 * Click Outside Handler for Drawer/Modal
 */
function setupClickOutsideHandler() {
  document.addEventListener('click', function (event) {
    // Unified drawer close handler
    const drawer = document.getElementById('stationDrawer');
    const isDrawerOpen = drawer && drawer.classList.contains('open');

    if (isDrawerOpen &&
      !event.target.closest('#stationDrawer') &&
      !event.target.closest('.station-circle') &&
      !event.target.closest('.station-label')) {
      window.hideStationDrawer();
    }
  });
}

/**
 * Slow Zone Event Setup
 * Adds click handlers to slow zone overlays
 */
function setupSlowZoneEvents(clickableArea, slowZone, lineData) {
  // Add title for hover tooltip
  const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
  title.textContent = `Slow Zone: ${slowZone.from} to ${slowZone.to} (${slowZone.direction}) - Click for details`;
  clickableArea.appendChild(title);

  // Add click event listener
  clickableArea.addEventListener("click", (e) => {
    e.stopPropagation();
    showSlowZoneTooltip(e, slowZone, lineData);
  });

  clickableArea.style.cursor = "pointer";
}

/**
 * Station Event Setup
 * Adds click handlers to station elements (circles and labels)
 */
function setupStationEvents(element, station) {
  element.style.cursor = "pointer";
  element.addEventListener("click", (e) => handleStationClick(e, station));

  // Add data attribute for station identification
  if (element.setAttribute) {
    element.setAttribute("data-station-name", station.name);
  }
}

/**
 * Map Drawing Prevention
 * Prevents multiple simultaneous map draws
 */
function preventMultipleDraws(drawingFlag, callback) {
  if (drawingFlag) {
    console.log('Map already being drawn - skipping');
    return false;
  }

  drawingFlag = true;
  try {
    callback();
  } finally {
    drawingFlag = false;
  }
  return true;
}

/**
 * Setup SVG Station Click Events
 * Adds event delegation for SVG station elements with data-station attributes
 */
function setupSVGStationEvents() {
  // Event delegation for SVG station clicks
  document.addEventListener('click', (event) => {
    const target = event.target;

    // Check if clicked element has data-station attribute
    let stationName = target.getAttribute('data-station');

    // If no data-station attribute, check if it's a text element with station name
    if (!stationName && target.tagName === 'text') {
      // For text elements, the station name might be in the text content
      const textContent = target.textContent.trim();
      if (textContent) {
        // Try to find station by the text content (could be short name or full name)
        const station = findStationByNameOrShortName(textContent);
        if (station) {
          // Get SVG ID for context
          let svgElement = target;
          while (svgElement && svgElement.tagName !== 'svg') {
            svgElement = svgElement.parentElement;
          }
          const svgId = svgElement?.id || null;
          
          handleStationClick(event, station, svgId);
          return;
        }
      }
    }

    // If no station name found, return early
    if (!stationName) return;

    console.log('SVG element clicked with data-station:', stationName);

    // Get SVG ID for context
    let svgElement = target;
    while (svgElement && svgElement.tagName !== 'svg') {
      svgElement = svgElement.parentElement;
    }
    const svgId = svgElement?.id || null;

    // Find the station object from the loaded data, passing the clicked element for context
    const station = findStationByName(stationName, target);
    if (!station) {
      console.warn(`Station not found: ${stationName}`);
      return;
    }

    // Handle the station click with SVG ID
    handleStationClick(event, station, svgId);
  });
}

/**
 * Find Station by Name
 * Searches through all line data to find a station by name
 * For interchange stations, tries to determine the correct line based on SVG context
 */
function findStationByName(stationName, clickedElement = null) {
  // Search through all available line data
  console.log(stationName, clickedElement)
  const lineDataSources = [
    { data: window.line1University, key: 'line1University' },
    { data: window.line1Yonge, key: 'line1Yonge' },
    { data: window.line2, key: 'line2' },
    { data: window.line4, key: 'line4' },
    { data: window.line5, key: 'line5' },
    { data: window.line6, key: 'line6' }
  ];

  // If we have a clicked element, try to determine which SVG/line it belongs to
  if (clickedElement) {
    let svgElement = clickedElement;
    while (svgElement && svgElement.tagName !== 'svg') {
      svgElement = svgElement.parentElement;
    }

    if (svgElement && svgElement.id) {
      const svgId = svgElement.id;
      console.log(`🔍 Finding station "${stationName}" from SVG: ${svgId}`);

      // Map SVG IDs to line data priority
      const priorityLineKey = SVG_ID_TO_LINE_KEY[svgId] || null;     

      // If we identified a priority line, search that first
      if (priorityLineKey) {
        console.log(`🔍 Priority line for SVG ${svgId}: ${priorityLineKey}`);
        const prioritySource = lineDataSources.find(source => source.key === priorityLineKey);
        if (prioritySource && prioritySource.data && prioritySource.data.stations) {
          const station = prioritySource.data.stations.find(s => s.name === stationName);
          if (station) {
            console.log(`✅ Found station ${stationName} in priority line ${priorityLineKey}`);
            return station;
          } else {
            console.log(`❌ Station ${stationName} NOT found in priority line ${priorityLineKey}`);
          }
        }
      } else {
        console.log(`⚠️ No priority line mapping found for SVG ID: ${svgId}`);
      }
    }
  }

  // Fallback to original search order if no priority match found
  console.log(`🔍 Falling back to search order for station "${stationName}"`);
  for (const lineSource of lineDataSources) {
    if (lineSource.data && lineSource.data.stations) {
      const station = lineSource.data.stations.find(s => s.name === stationName);
      if (station) {
        console.log(`✅ Found station ${stationName} in fallback line ${lineSource.key}`);
        return station;
      }
    }
  }

  console.log(`❌ Station ${stationName} not found in any line`);
  return null;
}

/**
 * Find Station by Name or Short Name
 * Searches through all line data to find a station by full name or short name
 */
function findStationByNameOrShortName(searchText) {
  // Search through all available line data
  const lineDataSources = [
    window.line1University,
    window.line1Yonge,
    window.line2,
    window.line4,
    window.line5,
    window.line6
  ];

  for (const lineData of lineDataSources) {
    if (lineData && lineData.stations) {
      // First try to find by exact name match
      let station = lineData.stations.find(s => s.name === searchText);
      if (station) {
        return station;
      }

      // Then try to find by short name match
      station = lineData.stations.find(s => s.shortName === searchText);
      if (station) {
        return station;
      }

      // Finally try case-insensitive partial matches
      station = lineData.stations.find(s =>
        s.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (s.shortName && s.shortName.toLowerCase().includes(searchText.toLowerCase()))
      );
      if (station) {
        return station;
      }
    }
  }

  return null;
}

/**
 * Initialize Slow Zone Click Handlers
 * Attaches click handlers to existing slow zone SVG elements
 */
function initializeSlowZoneHandlers() {
  // Find all slow zone path elements with data-id attributes
  const slowZoneElements = document.querySelectorAll('[data-id^="sz-"]');

  slowZoneElements.forEach(slowZoneElement => {
    // Get the slow zone ID from the data-id attribute
    const slowZoneId = slowZoneElement.getAttribute('data-id');
    if (!slowZoneId) return;

    // Determine which line this slow zone belongs to
    const svg = slowZoneElement.closest('svg');
    if (!svg || !svg.id) return;

    let lineData = null;
    let slowZones = [];

    // Map SVG ID to line data and slow zones
    // Lookup line key from the SVG ID
    const lineKey = SVG_ID_TO_LINE_KEY[svg.id];

    if (lineKey) {
        lineData = window[lineKey];
        slowZones = window[lineKey]?.slow_zones || [];
    } else {
        lineData = null;
        slowZones = [];
    }

    if (!lineData || slowZones.length === 0) return;

    // Find the specific slow zone by ID
    const specificSlowZone = slowZones.find(zone => zone.id === slowZoneId);
    if (!specificSlowZone) {
      console.warn(`Slow zone with ID ${slowZoneId} not found in data`);
      return;
    }

    // Make the element clickable
    slowZoneElement.style.cursor = 'pointer';

    // Add title for hover tooltip
    const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
    title.textContent = `Slow Zone: ${specificSlowZone.from} to ${specificSlowZone.to} - Click for details`;
    slowZoneElement.appendChild(title);

    // Add click event listener
    slowZoneElement.addEventListener('click', (e) => {
      e.stopPropagation();
      showSlowZoneTooltip(e, specificSlowZone, lineData);
    });
  });
}

/**
 * Initialize All Event Handlers
 */
function initializeEventHandlers() {
  setupLineSelectorEvents();
  setupClickOutsideHandler();
  setupSVGStationEvents();

  // Initialize slow zone handlers after a short delay to ensure SVGs are loaded
  setTimeout(() => {
    initializeSlowZoneHandlers();
  }, 500);

  // Window event listeners
  window.addEventListener('resize', handleWindowResize);

  // Initialize viewport size tracking
  window.lastKnownViewportSize = window.matchMedia('(max-width: 639px)').matches ? 'sm' : 'md-or-larger';
}

/**
 * DOM Content Loaded Handler
 */
document.addEventListener("DOMContentLoaded", function () {
  initializeEventHandlers();

  // Initialize default branch button styling
  setTimeout(() => {
    const universityButton = document.getElementById("line1-university-button");
    if (universityButton) {
      universityButton.style.backgroundColor = LINE_COLORS.line1University;
      universityButton.style.color = "#1F2937";
    }

    updateBranchButtonsVisibility();

    // Scroll to University branch on small screens
    if (window.matchMedia('(max-width: 639px)').matches) {
      const universityContainer = document.getElementById("line1-university-container");
      if (universityContainer) {
        universityContainer.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      }
    }
  }, 0);
});

// Export event handling functions
export {
  handleStationClick,
  toggleShortNames,
  setupLineSelectorEvents,
  updateLineVisibility,
  updateBranchButtonsVisibility,
  showSlowZoneTooltip,
  handleLineScroll,
  handleWindowResize,
  setupClickOutsideHandler,
  setupSlowZoneEvents,
  setupStationEvents,
  setupSVGStationEvents,
  findStationByName,
  findStationByNameOrShortName,
  initializeEventHandlers,
  preventMultipleDraws,
  currentTooltipStation,
  useShortNames,
  activeLineButton,
  activeBranchButton
};